/**
 * One-row option slider (Product Option Selector block).
 *
 * The block puts classes on its .mty container:
 *   vslider-color-desk / vslider-color-mob  -> colour swatches in one swipeable row
 *   vslider-pill-desk  / vslider-pill-mob   -> sizes & other pill options in one swipeable row
 *   vslider-noline                          -> no divider line between title and arrows
 * This script marks the matching <variant-pills> holders with data-vslider (CSS does the layout)
 * and adds prev/next buttons to the title row. Buttons hide when everything fits.
 */
(function () {
  if (window.__momentumVariantSlider) return;
  window.__momentumVariantSlider = true;

  const MOBILE = window.matchMedia('(max-width: 768px)');
  // chevron is drawn in CSS (.vslider-btn::before) - an inline SVG took the colour-scheme
  // background as stroke colour and disappeared on the live shop
  const ARROW = () => '';

  function modeFor(holder) {
    const box = holder.closest('.mty');
    if (!box) return null;
    const kind = holder.closest('.color-circles-snippet') ? 'color' : 'pill';
    return box.classList.contains(`vslider-${kind}-${MOBILE.matches ? 'mob' : 'desk'}`) ? kind : null;
  }

  function update(holder) {
    const nav = holder._vsliderNav;
    if (!nav) return;
    const active = holder.hasAttribute('data-vslider');
    const overflow = holder.scrollWidth > holder.clientWidth + 2;
    nav.hidden = !active || !overflow;
    if (holder._vsliderLine) holder._vsliderLine.hidden = !active || !overflow;
    const max = holder.scrollWidth - holder.clientWidth;
    const atStart = holder.scrollLeft <= 2;
    const atEnd = holder.scrollLeft >= max - 2;
    // edge fade on the side(s) where more options are
    holder.classList.toggle('vs-more-start', active && overflow && !atStart);
    holder.classList.toggle('vs-more-end', active && overflow && !atEnd);
    if (nav.hidden) return;
    nav.querySelector('[data-vslider-prev]').disabled = atStart;
    nav.querySelector('[data-vslider-next]').disabled = atEnd;
  }

  // Re-measure whenever the row or its content changes size (late CSS, fonts, images,
  // theme editor, hidden-then-shown sections). Measuring only once at load missed the
  // overflow on the live shop, so the arrows never appeared.
  const sizeWatch = typeof ResizeObserver === 'function'
    ? new ResizeObserver((entries) => entries.forEach((e) => {
      const holder = e.target.closest('variant-pills') || e.target;
      // first time the row really overflows: bring the selected option into view once
      if (!holder._vsCentered && holder.hasAttribute('data-vslider') && holder.scrollWidth > holder.clientWidth + 2) {
        holder._vsCentered = true;
        centerSelected(holder, false);
      }
      update(holder);
    }))
    : null;
  function watch(holder) {
    if (!sizeWatch || holder._vsWatched) return;
    holder._vsWatched = true;
    sizeWatch.observe(holder);
    Array.from(holder.children).forEach((child) => sizeWatch.observe(child));
  }

  function centerSelected(holder, smooth) {
    if (!holder.hasAttribute('data-vslider')) return;
    const checked = holder.querySelector('input:checked');
    if (!checked) return;
    const item = checked.closest('.variant-options-wrap') || checked.parentElement;
    if (!item) return;
    const h = holder.getBoundingClientRect();
    const r = item.getBoundingClientRect();
    const target = holder.scrollLeft + (r.left - h.left) - (h.width - r.width) / 2;
    holder.scrollTo({ left: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
  }

  function ensureNav(holder) {
    if (holder._vsliderNav) return;
    const container = holder.parentElement;
    const box = holder.closest('.mty');
    const nav = document.createElement('div');
    nav.className = 'vslider-nav';
    nav.hidden = true;
    const prevLabel = (box && box.dataset.vsliderPrev) || 'Previous';
    const nextLabel = (box && box.dataset.vsliderNext) || 'Next';
    nav.innerHTML =
      `<button type="button" class="vslider-btn" data-vslider-prev>${ARROW('prev')}</button>` +
      `<button type="button" class="vslider-btn" data-vslider-next>${ARROW('next')}</button>`;
    nav.querySelector('[data-vslider-prev]').setAttribute('aria-label', prevLabel);
    nav.querySelector('[data-vslider-next]').setAttribute('aria-label', nextLabel);

    const head = container.querySelector(':scope > .variant-option-head');
    const title = container.querySelector(':scope > .variant-element_title');
    if (head) {
      head.appendChild(nav); // size row with inline size chart: arrows go to its end
      head.classList.add('vslider-has-nav');
    } else {
      const row = document.createElement('div');
      row.className = 'vslider-head';
      container.insertBefore(row, title || holder);
      if (title) {
        row.appendChild(title);
        const line = document.createElement('span');
        line.className = 'vslider-line';
        line.setAttribute('aria-hidden', 'true');
        line.hidden = true;
        row.appendChild(line);
        holder._vsliderLine = line;
      }
      row.appendChild(nav);
    }

    const step = () => Math.max(80, holder.clientWidth * 0.8);
    nav.querySelector('[data-vslider-prev]').addEventListener('click', () => holder.scrollBy({ left: -step(), behavior: 'smooth' }));
    nav.querySelector('[data-vslider-next]').addEventListener('click', () => holder.scrollBy({ left: step(), behavior: 'smooth' }));
    let ticking = false;
    holder.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { ticking = false; update(holder); });
    }, { passive: true });
    holder.addEventListener('change', () => centerSelected(holder, true));
    holder._vsliderNav = nav;
  }

  /* ── Mobile-only dropdown ("Size style (mobile)" / "Other options style (mobile)") ──
     The pills stay the source of truth: the <select> only mirrors them and clicks the
     matching radio, so price, stock, gallery and cart logic run exactly as before. */
  function optionText(holder, input) {
    const label = (input.id && holder.querySelector(`label[for="${CSS.escape(input.id)}"]`)) || input.nextElementSibling;
    const span = label && label.querySelector('.variant-element_span > span');
    return ((span || label) ? (span || label).textContent : input.value).trim() || input.value;
  }

  function syncSelect(holder) {
    const sel = holder._vmdSelect;
    if (!sel) return;
    const unavailable = holder.dataset.unavailableText || '';
    const radios = Array.from(holder.querySelectorAll('input[type="radio"]'));
    sel.textContent = '';
    radios.forEach((radio) => {
      const opt = document.createElement('option');
      opt.value = radio.value;
      const soldOut = radio.classList.contains('disabled');
      opt.textContent = optionText(holder, radio) + (soldOut && unavailable ? ` – ${unavailable}` : '');
      opt.selected = radio.checked;
      sel.appendChild(opt);
    });
  }

  function ensureSelect(holder) {
    if (holder._vmdSelect) return;
    const wrap = document.createElement('div');
    wrap.className = 'vmd-wrap';
    const sel = document.createElement('select');
    sel.className = 'vmd-select';
    if (holder.dataset.optionLabel) sel.setAttribute('aria-label', holder.dataset.optionLabel);
    wrap.appendChild(sel);
    holder.insertAdjacentElement('afterend', wrap);
    sel.addEventListener('change', () => {
      const radio = Array.from(holder.querySelectorAll('input[type="radio"]')).find((r) => r.value === sel.value);
      if (radio && !radio.checked) radio.click();
    });
    holder.addEventListener('change', () => syncSelect(holder));
    // availability updates toggle the "disabled" class on the radios
    new MutationObserver(() => {
      clearTimeout(holder._vmdTimer);
      holder._vmdTimer = setTimeout(() => syncSelect(holder), 30);
    }).observe(holder, { subtree: true, attributes: true, attributeFilter: ['class'] });
    holder._vmdSelect = sel;
    holder._vmdWrap = wrap;
  }

  function applyDropdown(holder) {
    const want = MOBILE.matches && holder.dataset.mobileDropdown === 'true';
    if (want) {
      ensureSelect(holder);
      syncSelect(holder);
      holder.setAttribute('data-vmd-active', '');
      holder._vmdWrap.classList.add('is-active');
    } else if (holder._vmdWrap) {
      holder.removeAttribute('data-vmd-active');
      holder._vmdWrap.classList.remove('is-active');
    }
    return want;
  }

  function apply(holder) {
    if (applyDropdown(holder)) {
      // the dropdown replaces the row on mobile, so no slider for it
      if (holder.hasAttribute('data-vslider')) holder.removeAttribute('data-vslider');
      update(holder);
      return;
    }
    const mode = modeFor(holder);
    if (mode) {
      holder.setAttribute('data-vslider', mode);
      ensureNav(holder);
      watch(holder);
      requestAnimationFrame(() => { centerSelected(holder, false); update(holder); });
    } else if (holder.hasAttribute('data-vslider')) {
      holder.removeAttribute('data-vslider');
      update(holder);
    }
  }

  const all = () => document.querySelectorAll('variant-pills.variant-options-holder');
  function applyAll() { all().forEach(apply); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', applyAll);
  else applyAll();
  window.addEventListener('load', () => all().forEach(update));
  (MOBILE.addEventListener ? MOBILE.addEventListener('change', applyAll) : MOBILE.addListener(applyAll));
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => all().forEach(update), 120);
  });
  document.addEventListener('shopify:section:load', applyAll);
  new MutationObserver((records) => {
    for (const rec of records) {
      for (const n of rec.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches('variant-pills.variant-options-holder')) apply(n);
        else if (n.querySelectorAll) n.querySelectorAll('variant-pills.variant-options-holder').forEach(apply);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
